import {
    defineEntityConstellationDerivation,
    defineEntityItemDerivation,
    toEntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import { buildCylinderWallShape } from '@toolproof-core/visualization/_pure/shapes';
import type {
    EntityConstellationDerivation,
    EntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import { toPrimitiveBandPosition } from '@/adapters/primitive/_pure/derivation/constellations';
import {
    toSettlementWallCollectionKey,
    toSettlementWallConstellationKey,
    toSettlementWallItemKey,
} from '@/adapters/primitive/_pure/derivation/keyCodec';
import type { Config, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';

type Vector3 = readonly [number, number, number];

/**
 * Encloses every composite settlement unit in translucent walls running from its Source bands to its Target bands.
 * Expects placed wheels, so the walls follow whatever positions the wheels finally render at.
 */
export function buildSettlementWallDerivations(args: {
    config: Config;
    wheels: readonly PrimitiveWheelState[];
    axis: Vector3;
}): { entityItemCollectionDerivations: EntityItemCollectionDerivation[]; entityConstellationDerivations: EntityConstellationDerivation[] } {
    const { config, wheels, axis } = args;
    const preview = config.natural.display.settlementPreview;
    const result = {
        entityItemCollectionDerivations: [] as EntityItemCollectionDerivation[],
        entityConstellationDerivations: [] as EntityConstellationDerivation[],
    };
    if (!preview.visible || !preview.wall.visible) return result;
    const { wall } = preview;
    const { innerRadius: hubRadius, ringWidth } = config.base.display.wheel;
    const ringGap = config.natural.display.wheel.ringGap;
    const bandInnerRadius = (power: number) => hubRadius + power * (ringWidth + ringGap);

    const unitKeys = [...new Set(wheels.flatMap((wheel) => wheel.settlementUnit?.unitKey ?? []))];
    for (const unitKey of unitKeys) {
        const members = wheels.filter((wheel) => wheel.settlementUnit?.unitKey === unitKey);
        const target = members.find((wheel) => wheel.settlementUnit?.participant === 'target');
        const sources = members.filter((wheel) => wheel.settlementUnit?.participant === 'source');
        if (target === undefined || sources.length === 0) continue;
        const opacity = target.settlementUnit!.opacity;
        const sourceAt = (power: number) => sources.find((wheel) => wheel.settlementUnit!.powers.includes(power));
        const powers = [...new Set(sources.flatMap((wheel) => wheel.settlementUnit!.powers))].sort((left, right) => left - right);
        const walls = wall.groupingMode === 'envelope'
            ? [{ part: 'envelope' as const, lowest: powers[0]!, highest: powers[powers.length - 1]! }]
            : powers.map((power) => ({ part: power, lowest: power, highest: power }));

        for (const { part, lowest, highest } of walls) {
            const source = sourceAt(lowest);
            if (source === undefined) continue;
            const from = toPrimitiveBandPosition(source, lowest);
            const to = toPrimitiveBandPosition(target, lowest);
            const ref = { unitKey, part };
            const itemKey = toSettlementWallItemKey(ref);
            result.entityItemCollectionDerivations.push(toEntityItemCollectionDerivation({
                itemCollectionKey: toSettlementWallCollectionKey(ref),
                itemDerivations: [defineEntityItemDerivation({
                    item: {
                        itemKind: 'entity',
                        itemKey,
                        name: `Settlement wall ${part}`,
                        description: 'Encloses the two wheels of one settlement as a single unit.',
                    },
                    parametricState: {
                        shape: buildCylinderWallShape({
                            innerRadius: bandInnerRadius(lowest),
                            outerRadius: bandInnerRadius(highest) + ringWidth,
                            height: Math.hypot(to[0] - from[0], to[1] - from[1], to[2] - from[2]),
                            axis: [...axis],
                            arcSegments: config.base.display.wheel.arcSegments * 2,
                            innerOpacity: wall.innerOpacity,
                            outerOpacity: wall.outerOpacity,
                            cutaway: true,
                        }),
                        visualStyle: { color: wall.color, opacity },
                        cameraProximityFade: { ...wall.cameraFade },
                    },
                })],
            }));
            result.entityConstellationDerivations.push(defineEntityConstellationDerivation({
                constellation: {
                    constellationKind: 'entity',
                    constellationKey: toSettlementWallConstellationKey(ref),
                    itemCollectionKey: toSettlementWallCollectionKey(ref),
                    anchor: { itemKey, anchorKey: 'center' },
                },
                parametricState: {
                    layout: {
                        layoutKind: 'ring',
                        anchor: {
                            anchorKind: 'position',
                            position: [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2, (from[2] + to[2]) / 2],
                        },
                        radius: 0,
                    },
                    ringGuide: false,
                },
            }));
        }
    }
    return result;
}
